import { assert } from '../app/logger.js';
import { UIMixin } from '../ui/ui.js';
import { window } from '../window.js';

const isArrowFunction = fn =>
  typeof fn === 'function' && /^[^{]+?=>/.test(fn.toString());

/**
 * HTML control.
 */
class HTMLControl extends UIMixin(window.HTMLElement) {
  static defineCustomElement() {
    window.customElements.define(this.tagName, this);
  }

  constructor() {
    super();

    let o = this;
    while ((o = Object.getPrototypeOf(o))) {
      if (
        !Object.prototype.hasOwnProperty.call(o.constructor, 'properties') ||
        Object.prototype.hasOwnProperty.call(
          o.constructor,
          '_connectedProperties'
        )
      ) {
        continue;
      }
      o.constructor._connectedProperties = [];
      const { properties } = o.constructor;
      for (let name in properties) {
        let options = properties[name];
        o.constructor._connectedProperties.push([name, options]);

        // Methods.
        if (options instanceof Function) {
          o.constructor.prototype[name] = function (v) {
            this[`_${name}`] = v;
            return this.isInitialized && options.call(this, this[`_${name}`]);
          };
          continue;
        }

        // Properties
        for (let funcname of ['set', 'get', 'preset', 'postget']) {
          assert(
            !options[funcname] || !isArrowFunction(options[funcname]),
            `Arrow function cannot be used to define a connected property`
          );
        }

        let { attribute } = options;
        let attribute_name =
          (attribute && typeof attribute == 'object' && attribute.name) || name;
        let attribute_type =
          (attribute && typeof attribute == 'object' && attribute.type) ||
          attribute;
        let attribute_set =
          (attribute && typeof attribute == 'object' && attribute.set) || null;
        let attribute_get =
          (attribute && typeof attribute == 'object' && attribute.get) || null;

        // Define cached value getter/setter.
        Object.defineProperty(o.constructor.prototype, `${name}Cached`, {
          get() {
            let v = undefined;
            if (attribute_type == 'boolean') {
              v = this.hasAttribute(attribute_name);
            } else if (attribute_type && this.hasAttribute(attribute_name)) {
              v = this.getAttribute(attribute_name);
              if (attribute_get) {
                v = attribute_get(v);
              }
            } else {
              v = this[`_${name}`];
            }
            return v;
          },
          set(v) {
            if (attribute_type == 'boolean') {
              v
                ? this.setAttribute(attribute_name, attribute_name)
                : this.removeAttribute(attribute_name);
            } else if (attribute_type) {
              if (attribute_set) {
                v = attribute_set(v);
              }
              this.setAttribute(attribute_name, v);
            } else {
              this[`_${name}`] = v;
            }
          },
        });

        // Define value getter/setter.
        Object.defineProperty(o.constructor.prototype, name, {
          get() {
            let v = undefined;
            if (options.get && this.isInitialized) {
              v = options.get.call(this);
            } else {
              v = this[`${name}Cached`];
            }
            return options.postget ? options.postget.call(this, v) : v;
          },
          set(v) {
            if (options.preset) {
              v = options.preset.call(this, v);
            }
            this[`${name}Cached`] = v;
            if (this.isInitialized && options.set) {
              options.set.call(this, v);
            }
          },
        });
      }
    }
  }

  initialize() {
    // Append the fragment if no children yet.
    if (!this.firstElementChild) {
      this.appendChild(this.fragment);
    }

    let trace = new Set();
    let o = this;
    while ((o = Object.getPrototypeOf(o))) {
      if (
        Object.prototype.hasOwnProperty.call(
          o.constructor,
          '_connectedProperties'
        )
      ) {
        for (let [name, options] of o.constructor._connectedProperties) {
          // Do not call a property from a base class if it was overriden by
          // a derived class.
          if (trace.has(name)) {
            continue;
          }
          trace.add(name);

          if (options.set) {
            // set property
            let value = this[`${name}Cached`];
            if (options.initialize || value !== undefined) {
              options.set.call(this, value);
            }
          } else if (this[`_${name}`] !== undefined) {
            // set method, note: can be async
            options.call(this, this[`_${name}`]);
          }
        }
      }
    }
  }

  connectedCallback() {
    if (!this.isInitialized) {
      this.isInitialized = true;
      this.initialize();
      return;
    }
  }

  /**
   * Returns the control's markup.
   **/
  static markup = '';

  /**
   * Returns connected properties represented by a map of property names and
   * their info. Each property is described by
   *   get - to get a value
   *   set - to set a value
   *   postget - to transform get value after getting
   *   preset - to transform set value before setting
   *   attribute - to reflect property into DOM attribute
   *     boolean - to reflect property to boolean DOM attribute
   */
  static properties = {};
}

export { HTMLControl };
